// src/components/documents/CatalogQuickAdd.js — doced-8f3a61c2 (Document editor, Stage 4)
//
// The dashed box under the line items: type, and up to 5 catalog matches appear (searchCatalogPicker,
// after a short pause in typing), plus "Add 'x' as a one-off line" and "Browse the catalog…".
// Arrow keys move through the choices, Enter picks, Escape closes the list.
//
// Round 1 fix: the list floats ABOVE the page (rendered into document.body, fixed-positioned against
// the box), so the line-item table's sideways scroll can no longer clip it. It opens downward when
// there is room and flips upward when there isn't; it follows the box on scroll / resize and closes
// on a click anywhere else.

import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { useQuickCatalogSearch } from './useCatalogSearch';
import { formatCurrency } from '../../utils/formatters';

const GAP = 6;
const MAX_HEIGHT = 360;

/** Where the floating list goes: below the box when it fits, else above (whichever has more room). Pure. */
export function popoverPlacement(rect, viewportHeight, listHeight) {
    const want = Math.min(listHeight || MAX_HEIGHT, MAX_HEIGHT);
    const below = viewportHeight - rect.bottom - GAP - 8;
    const above = rect.top - GAP - 8;
    const up = below < want && above > below;
    return {
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(120, Math.min(MAX_HEIGHT, up ? above : below)),
        ...(up ? { bottom: viewportHeight - rect.top + GAP } : { top: rect.bottom + GAP }),
        up,
    };
}

export default function CatalogQuickAdd({ onPick, onOneOff, onBrowse, pathOf, currency }) {
    const [text, setText] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [place, setPlace] = useState(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const listId = useId();
    const search = useQuickCatalogSearch(text);
    const q = text.trim();
    const show = open && q.length > 0;

    const choices = [
        ...search.items.map((it) => ({ kind: 'item', item: it })),
        { kind: 'oneoff' },
        { kind: 'browse' },
    ];
    const close = () => { setOpen(false); setActive(-1); };
    const reset = () => { setText(''); close(); };
    const choose = (c) => {
        if (!c) return;
        if (c.kind === 'item') onPick(c.item);
        else if (c.kind === 'oneoff') onOneOff(q);
        else onBrowse(q);
        reset();
        if (c.kind !== 'browse' && inputRef.current) inputRef.current.focus();
    };
    const onKeyDown = (e) => {
        if (e.key === 'Escape') { if (show) { e.preventDefault(); close(); } return; }
        if (!q) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => (a + 1) % choices.length); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setActive((a) => (a <= 0 ? choices.length - 1 : a - 1)); }
        else if (e.key === 'Enter') { e.preventDefault(); choose(choices[active >= 0 ? active : 0]); }
    };

    // Position against the box; again whenever the page scrolls or resizes, or the list changes size.
    useLayoutEffect(() => {
        if (!show) { setPlace(null); return undefined; }
        const update = () => {
            const box = inputRef.current;
            if (!box) return;
            setPlace(popoverPlacement(box.getBoundingClientRect(), window.innerHeight, listRef.current ? listRef.current.scrollHeight : MAX_HEIGHT));
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
    }, [show, search.status, search.items.length]);

    // A click anywhere but the box or the list closes it.
    useEffect(() => {
        if (!show) return undefined;
        const away = (e) => {
            if (inputRef.current && inputRef.current.contains(e.target)) return;
            if (listRef.current && listRef.current.contains(e.target)) return;
            close();
        };
        document.addEventListener('mousedown', away);
        document.addEventListener('touchstart', away);
        return () => { document.removeEventListener('mousedown', away); document.removeEventListener('touchstart', away); };
    }, [show]);

    // Keep the highlighted choice in view.
    useEffect(() => {
        if (active < 0 || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-opt="${active}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [active]);

    const optId = (i) => `${listId}-opt-${i}`;
    const rowCls = (i) => `flex w-full items-center justify-between gap-3 min-h-[48px] px-4 text-left text-sm border-b border-gray-100 ${active === i ? 'bg-[#5F4EC9]/10' : 'bg-white hover:bg-gray-50'}`;
    const oneOffIndex = search.items.length;
    const opt = (i, kind, extra, children) => (
        <div key={`${kind}-${i}`} id={optId(i)} data-opt={i} role="option" aria-selected={active === i}
            onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => choose(choices[i])}
            className={`${rowCls(i)} cursor-pointer ${extra || ''}`}>{children}</div>
    );

    const list = show && (
        <div ref={listRef} id={listId} role="listbox" aria-label="Matching catalog items"
            style={{ position: 'fixed', left: place?.left ?? -9999, width: place?.width ?? 480, top: place?.top, bottom: place?.bottom, maxHeight: place?.maxHeight ?? MAX_HEIGHT, visibility: place ? 'visible' : 'hidden' }}
            className="z-[80] bg-white border border-gray-200 rounded-xl shadow-xl overflow-y-auto">
            {q.length < 2 && <div className="px-4 py-3 text-xs text-gray-500" role="presentation">Keep typing to search the catalog…</div>}
            {search.status === 'loading' && (
                <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2" role="presentation"><Loader2 size={14} className="animate-spin" aria-hidden="true" />Searching…</div>
            )}
            {search.status === 'error' && <div className="px-4 py-3 text-sm text-red-600" role="presentation">{search.error}</div>}
            {search.status === 'done' && search.items.length === 0 && (
                <div className="px-4 py-3 text-sm text-gray-500" role="presentation">No catalog items match “{q}”.</div>
            )}
            {search.items.map((it, i) => {
                const path = pathOf(it);
                return opt(i, 'item', '', (
                    <>
                        <span className="min-w-0 truncate"><strong>{it.name}</strong>{path && <span className="text-gray-500 text-xs"> · {path}</span>}</span>
                        <span className="font-semibold tabular-nums shrink-0">{formatCurrency(Number(it.price) || 0, currency)}</span>
                    </>
                ));
            })}
            {opt(oneOffIndex, 'oneoff', '', <span className="text-gray-700 truncate">Add “{q}” as a one-off line</span>)}
            {opt(oneOffIndex + 1, 'browse', `border-b-0 font-bold text-[#4f40b0] ${active === oneOffIndex + 1 ? '' : '!bg-gray-50'}`, 'Browse the catalog…')}
        </div>
    );

    return (
        <div className="relative w-full max-w-[480px]">
            <label htmlFor={`${listId}-input`} className="sr-only">Add an item</label>
            <input
                id={`${listId}-input`}
                ref={inputRef}
                type="text"
                value={text}
                onChange={(e) => { setText(e.target.value); setOpen(true); setActive(-1); }}
                onFocus={() => setOpen(true)}
                onBlur={(e) => { if (!(listRef.current && e.relatedTarget && listRef.current.contains(e.relatedTarget))) close(); }}
                onKeyDown={onKeyDown}
                autoComplete="off"
                role="combobox"
                aria-expanded={!!show}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={show && active >= 0 ? optId(active) : undefined}
                placeholder="+ Type to add an item (or a one-off line)…"
                className="w-full h-[44px] px-3 text-[15px] border border-dashed border-[#5F4EC9]/50 rounded-lg bg-[#5F4EC9]/[0.03] focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/40"
            />
            {list && createPortal(list, document.body)}
        </div>
    );
}
